import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Question {
  id: string;
  title: string;
  description: string;
  type: 'radio' | 'text' | 'textarea';
  options?: { value: string; label: string }[];
  validation?: (value: string) => boolean;
  required?: boolean;
}

const investorQuestions: Question[] = [
  {
    id: 'investment_range',
    title: 'What is your investment range?',
    description: 'Help us understand your investment capacity',
    type: 'radio',
    options: [
      { value: '0-50k', label: 'R0 - R50,000' },
      { value: '50k-250k', label: 'R50,000 - R250,000' },
      { value: '250k-1m', label: 'R250,000 - R1,000,000' },
      { value: '1m+', label: 'R1,000,000+' }
    ],
    required: true
  },
  {
    id: 'investment_style',
    title: 'What is your preferred investment style?',
    description: 'Tell us about your investment approach',
    type: 'radio',
    options: [
      { value: 'active', label: 'Active (Hands-on involvement)' },
      { value: 'passive', label: 'Passive (Financial investment only)' }
    ],
    required: true
  },
  {
    id: 'preferred_stages',
    title: 'Which business stages interest you?',
    description: 'Select your preferred investment stages',
    type: 'radio',
    options: [
      { value: 'seed', label: 'Seed Stage' },
      { value: 'early', label: 'Early Stage' },
      { value: 'growth', label: 'Growth Stage' },
      { value: 'mature', label: 'Mature Business' }
    ],
    required: true
  },
  {
    id: 'preferred_sectors',
    title: 'Which sectors do you prefer to invest in?',
    description: 'Select your preferred industry sectors',
    type: 'radio',
    options: [
      { value: 'technology', label: 'Technology & Software' },
      { value: 'healthcare', label: 'Healthcare & Biotech' },
      { value: 'finance', label: 'Finance & Fintech' },
      { value: 'retail', label: 'Retail & E-commerce' },
      { value: 'energy', label: 'Energy & Sustainability' },
      { value: 'other', label: 'Other Industries' }
    ],
    required: true
  },
  {
    id: 'investment_timeline',
    title: 'What is your investment timeline?',
    description: 'How long do you plan to hold your investments?',
    type: 'radio',
    options: [
      { value: 'short_term', label: '1-2 years' },
      { value: 'medium_term', label: '3-5 years' },
      { value: 'long_term', label: '5+ years' }
    ],
    required: true
  },
  {
    id: 'additional_notes',
    title: 'Additional Investment Preferences',
    description: 'Tell us more about your investment criteria and expectations',
    type: 'textarea',
    required: false
  }
];

const entrepreneurQuestions: Question[] = [
  {
    id: 'experience',
    title: 'What is your entrepreneurial experience?',
    description: 'Tell us about your background',
    type: 'radio',
    options: [
      { value: 'first_time', label: 'First-time Entrepreneur' },
      { value: '1_5_years', label: '1-5 years experience' },
      { value: '5_10_years', label: '5-10 years experience' },
      { value: '10_plus_years', label: '10+ years experience' }
    ],
    required: true
  },
  {
    id: 'business_stage',
    title: 'What stage is your business in?',
    description: 'Help us understand where you are in your journey',
    type: 'radio',
    options: [
      { value: 'idea', label: 'Idea/Concept Stage' },
      { value: 'development', label: 'Development/MVP' },
      { value: 'market', label: 'In Market' },
      { value: 'growth', label: 'Growth/Scaling' }
    ],
    required: true
  },
  {
    id: 'funding_needs',
    title: 'What are your funding requirements?',
    description: 'Select your target funding range',
    type: 'radio',
    options: [
      { value: '0-50k', label: 'R0 - R50,000' },
      { value: '50k-250k', label: 'R50,000 - R250,000' },
      { value: '250k-1m', label: 'R250,000 - R1,000,000' },
      { value: '1m+', label: 'R1,000,000+' }
    ],
    required: true
  },
  {
    id: 'business_sector',
    title: 'Which sector is your business in?',
    description: 'Select your primary industry sector',
    type: 'radio',
    options: [
      { value: 'technology', label: 'Technology & Software' },
      { value: 'healthcare', label: 'Healthcare & Biotech' },
      { value: 'finance', label: 'Finance & Fintech' },
      { value: 'retail', label: 'Retail & E-commerce' },
      { value: 'energy', label: 'Energy & Sustainability' },
      { value: 'other', label: 'Other Industries' }
    ],
    required: true
  },
  {
    id: 'team_size',
    title: 'What is your current team size?',
    description: 'Tell us about your team',
    type: 'radio',
    options: [
      { value: 'solo', label: 'Solo Founder' },
      { value: 'small', label: '2-5 team members' },
      { value: 'medium', label: '6-15 team members' },
      { value: 'large', label: '15+ team members' }
    ],
    required: true
  },
  {
    id: 'business_description',
    title: 'Tell us about your business',
    description: 'Provide a brief description of your business, its current state, and your vision',
    type: 'textarea',
    required: true,
    validation: (value) => value.length >= 100
  }
];

export default function OnboardingWizard() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, userRole } = useAuth();
  const navigate = useNavigate();

  // Select questions based on user role
  const questions = userRole === 'investor' ? investorQuestions : entrepreneurQuestions;
  const currentQuestion = questions[currentStep];
  const progress = ((currentStep + 1) / questions.length) * 100;

  const handleAnswer = (value: string) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: value
    }));
  };

  const isCurrentAnswerValid = () => {
    const answer = answers[currentQuestion.id];
    if (!answer && currentQuestion.required) return false;
    if (currentQuestion.validation && !currentQuestion.validation(answer)) return false;
    return true;
  };

  const handleNext = () => {
    if (!isCurrentAnswerValid()) {
      toast.error('Please provide a valid answer before proceeding');
      return;
    }
    
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      if (!user?.id) {
        toast.error('User session not found');
        return;
      }

      // Save questionnaire responses based on role
      if (userRole === 'investor') {
        // Format the data for investor preferences
        const investorData = {
          user_id: user.id,
          investment_range: answers.investment_range || '0-50k',
          investment_style: answers.investment_style || 'passive',
          preferred_stages: answers.preferred_stages ? [answers.preferred_stages] : ['seed'],
          preferred_sectors: answers.preferred_sectors ? [answers.preferred_sectors] : ['technology'],
          investment_timeline: answers.investment_timeline || 'medium_term',
          additional_notes: answers.additional_notes || ''
        };

        console.log('Submitting investor data:', investorData);

        // First try to delete any existing preferences
        await supabase
          .from('investor_preferences')
          .delete()
          .eq('user_id', user.id);

        // Save investor preferences
        const { error: investorError } = await supabase
          .from('investor_preferences')
          .insert(investorData);

        if (investorError) {
          console.error('Investor preferences error:', investorError);
          throw investorError;
        }
      } else {
        // Format the data for entrepreneur profile
        const entrepreneurData = {
          user_id: user.id,
          experience: answers.experience || 'first_time',
          skills: ['business_development'], // Default skill
          values: ['innovation'], // Default value
          additional_notes: answers.business_description || ''
        };

        console.log('Submitting entrepreneur data:', entrepreneurData);

        // First try to delete any existing profile
        await supabase
          .from('entrepreneur_profiles')
          .delete()
          .eq('user_id', user.id);

        // Save entrepreneur profile
        const { error: entrepreneurError } = await supabase
          .from('entrepreneur_profiles')
          .insert(entrepreneurData);

        if (entrepreneurError) {
          console.error('Entrepreneur profile error:', entrepreneurError);
          throw entrepreneurError;
        }
      }

      // Update user profile with onboarding completion
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ onboarding_completed: true })
        .eq('user_id', user.id);

      if (profileError) {
        console.error('Profile update error:', profileError);
        throw profileError;
      }

      // Clear the onboarding status cache
      localStorage.removeItem(`onboarding-${user.id}`);
      // Set the new cache value
      localStorage.setItem(`onboarding-${user.id}`, JSON.stringify(true));

      toast.success('Thank you for completing the questionnaire!');
      
      // Redirect based on role
      if (userRole === 'investor') {
        navigate('/investor');
      } else if (userRole === 'entrepreneur') {
        navigate('/entrepreneur');
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      toast.error('Failed to submit questionnaire. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900">Welcome to H44S</h2>
          <p className="mt-2 text-lg text-gray-600">
            Let's get to know you better to provide the best experience
          </p>
        </div>

        <Progress value={progress} className="w-full" />

        <Card>
          <CardHeader>
            <CardTitle>{currentQuestion.title}</CardTitle>
            <CardDescription>{currentQuestion.description}</CardDescription>
          </CardHeader>
          <CardContent>
            {currentQuestion.type === 'radio' && currentQuestion.options && (
              <RadioGroup
                value={answers[currentQuestion.id] || ''}
                onValueChange={handleAnswer}
              >
                <div className="space-y-4">
                  {currentQuestion.options.map((option) => (
                    <div key={option.value} className="flex items-center">
                      <RadioGroupItem value={option.value} id={option.value} />
                      <Label htmlFor={option.value} className="ml-3">{option.label}</Label>
                    </div>
                  ))}
                </div>
              </RadioGroup>
            )}

            {currentQuestion.type === 'text' && (
              <Input
                value={answers[currentQuestion.id] || ''}
                onChange={(e) => handleAnswer(e.target.value)}
                placeholder="Type your answer here..."
              />
            )}

            {currentQuestion.type === 'textarea' && (
              <Textarea
                value={answers[currentQuestion.id] || ''}
                onChange={(e) => handleAnswer(e.target.value)}
                placeholder="Type your detailed answer here..."
                className="min-h-[100px]"
              />
            )}
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 0 || isSubmitting}
            >
              Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={!isCurrentAnswerValid() || isSubmitting}
            >
              {currentStep === questions.length - 1 ? 'Submit' : 'Next'}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
} 